import React, { Context, createContext, useContext, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AvalanchePathImagesService, {
  AVALANCHE_PATH_IMAGES_PARAMS,
  AvalanchePathImagesDTO,
} from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesService';
import { CrudCreate, CrudDelete, CrudEdit } from '@awp/shared/Crud';
import { toast } from 'react-toastify';

const AvalanchePathImagesContext: Context<any> = createContext(null);

export const AvalanchePathImagesContextProvider = (props: any) => {
  const { children, ...rest } = props;

  const avalanchePathImagesService = new AvalanchePathImagesService();

  const { id: parentEntityId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [entity, setEntity] = useState<any>(null); // TODO: Define type
  const [mediaEntities, setMediaEntities] = useState<AvalanchePathImagesDTO[]>([]);
  const [filteredData, setFilteredData] = useState<AvalanchePathImagesDTO[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState(searchParams.get(AVALANCHE_PATH_IMAGES_PARAMS.SEARCH) ?? '');
  const [eventErrors, setEventErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  const onHideEdit = () => {
    setShowEditModal(false);
    setEntity(null);
  };

  const onHandleEdit = (values: any, myProps: any) => {
    if (!parentEntityId) return;

    const { setSubmitting, setErrors, setEventErrors, ...rest } = myProps;
    const { successMessage, errorMessage } = new CrudEdit(values?.Name);
    const avalanchePathImagesDto = avalanchePathImagesService.mapFormToDTO({ entity: values });

    setSubmitting(true);
    setLoading(true);

    const $promise = new Promise((resolve, reject) => {
      return avalanchePathImagesService
        .putAvalanchePathImage(parentEntityId, avalanchePathImagesDto)
        .then(async (response: any) => {
          if (response.ok) {
            const avalancheImages = await AvalanchePathImagesService.getAvalanchePathImages(parentEntityId);
            setMediaEntities(avalancheImages);
          } else if (response.status === 412) {
            const errors = await response.json();
            setErrors(errors);
            if (errors.entity) setEventErrors({ Entity: errors.entity });
            reject({});
          }
        })
        .finally(() => {
          setSubmitting(false);
          setLoading(false);
          onHideEdit();
          resolve({});
        });
    });

    toast.promise($promise, {
      pending: 'Updating...',
      success: successMessage,
      error: errorMessage,
    });
  };

  const onHideCreate = () => {
    setShowCreateModal(false);
  };

  const onHandleCreate = (values: any, myProps: any) => {
    if (!parentEntityId) return;

    const { setSubmitting, setErrors, setEventErrors, ...rest } = myProps;
    const { successMessage, errorMessage } = new CrudCreate(values?.Name);
    const avalanchePathImagesDto = avalanchePathImagesService.mapFormToDTO({ entity: values });

    setSubmitting(true);
    setLoading(true);

    const $promise = new Promise((resolve, reject) => {
      return avalanchePathImagesService
        .postAvalanchePathImage(parentEntityId, avalanchePathImagesDto)
        .then(async (response: any) => {
          if (response.ok) {
            const avalancheImages = await AvalanchePathImagesService.getAvalanchePathImages(parentEntityId);
            setMediaEntities(avalancheImages);
          } else if (response.status === 412) {
            const errors = await response.json();
            setErrors(errors);
            if (errors.entity) setEventErrors({ Entity: errors.entity });
            reject({});
          }
        })
        .finally(() => {
          setSubmitting(false);
          setLoading(false);
          onHideCreate();
          resolve({});
        });
    });

    toast.promise($promise, {
      pending: 'Creating...',
      success: successMessage,
      error: errorMessage,
    });
  };

  const onDelete = () => {
    if (!parentEntityId) return;

    const { successMessage, errorMessage } = new CrudDelete(entity.Name);
    const $promise = new Promise((resolve, reject) => {
      return AvalanchePathImagesService.deleteAvalanchePathImage(parentEntityId, entity?.Id).then((response: any) => {
        if (response.ok) {
          // Remove deleted row
          setMediaEntities(
            mediaEntities?.filter((ap: any) => {
              return ap.Id !== entity?.Id;
            }),
          );

          setShowDeleteModal(false);
          setEntity(null);
          resolve({});
        } else {
          // TODO: Handle 412 status
          reject({});
        }
      });
    });

    toast.promise($promise, {
      pending: 'Deleting...',
      success: successMessage,
      error: errorMessage,
    });
  };

  const stateValues = {
    parentEntityId,
    entity,
    mediaEntities,
    filteredData,
    showEditModal,
    showDeleteModal,
    showCreateModal,
    searchParams,
    search,
    eventErrors,
    loading,
  };

  const stateSetters = {
    setEntity,
    setShowEditModal,
    setMediaEntities,
    setFilteredData,
    setShowDeleteModal,
    setShowCreateModal,
    setSearchParams,
    setSearch,
    setEventErrors,
    setLoading,
  };

  const handlers = {
    onHideEdit,
    onHandleEdit,
    onHideCreate,
    onHandleCreate,
    onDelete,
  };

  const contextValues: any = {
    ...stateValues,
    ...stateSetters,
    ...handlers,
  };

  return <AvalanchePathImagesContext.Provider value={contextValues}>{children}</AvalanchePathImagesContext.Provider>;
};

export const useAvalanchePathImagesContext = () => useContext(AvalanchePathImagesContext);
